// Reshapes the `application.applicants` array ApplicationProfile already
// loads (via Redux, not React Query) into the same household-member shape
// useHouseholdMembersQuery.helpers.js's buildHouseholdMembers/
// buildHouseholdPendingMembers already produce for a resident's household --
// so useMiniProfileSections/buildSavePayload/MiniProfileBridgeContent don't
// need to know which context they're rendering for.
//
// Every member here is pre-move-in: there is no Resident row yet, adult or
// minor. `status: 'Pending'` is applied uniformly (not just to adults) so
// Start/End Date -- which useMiniProfileSections renders for both member
// types -- gets the same "no record to date yet" disabling the existing
// Pending-Applicant path already relies on (datesDisabled in
// useMiniProfileSections), instead of being enabled-and-required with
// nothing legitimate to put in it. The side effect is that Personal/
// Affordable also render read-only+bannered for a minor here, same as an
// adult -- a deliberate deviation from this card's own ASSUMPTION 1 (which
// expected minors to stay fully editable); flagged in this card's report as
// something to confirm, not a resolved decision.
import { parseResidentInfo } from './useHouseholdMembersQuery.helpers';

// Household.mainCustomerId is the actual source of truth for the applicant
// stage's Head of Household (see R4 in the ticket's spec), but
// ApplicantCustomer.isProspect is the field the codebase already treats as
// its stand-in everywhere else on this stage (ApplicationProfile/selectors.js
// getApplicationHousehold's own `isPrimary: applicantCustomer.isProspect`,
// and EditApplicationHousehold's `adults.find(a => a.isProspect)` for
// primaryApplicantId) -- reusing it here instead of inventing a second
// convention. Populating this correctly is what keeps the Delete button's
// head-of-household guard (MiniProfileBridge/index.js's resolveDeleteBlocked
// Reason, keyed on occupantInfo.isPrimary) from failing open on the main
// customer.
const buildApplicantAdultMember = (applicant) => {
  const customer = applicant?.applicantCustomer?.customer ?? {};
  const isPrimary = Boolean(applicant?.applicantCustomer?.isProspect);
  const residentInfo = parseResidentInfo(
    applicant,
    ['applicantCustomer', 'customer'],
    'adult',
    isPrimary,
    applicant?.relationshipId ?? 'default',
    applicant?.applicantCustomer?.address ?? {},
    customer,
    '',
  );

  return {
    id: applicant.id,
    applicantId: applicant.id,
    customerId: customer.id ?? null,
    name: [
      customer.firstName,
      customer.lastName,
      customer?.suffix ? customer.suffix.name : '',
    ].join(' '),
    status: 'Pending',
    type: 'adult',
    relationship: applicant?.relationship?.name ?? '---',
    relationshipId: applicant?.relationshipId,
    applicantTypeId: applicant?.applicantTypeId ?? '',
    // Display text for ChangeHeadOfHouseholdModal's occupant descriptor
    // (Household/ChangeHeadOfHousehold/index.js's getOccupantDescriptor) --
    // the resident-shaped candidate list already carries this same field
    // (useHouseholdMembersQuery.helpers.js).
    applicantType: applicant?.applicantType?.name ?? '',
    affordableRelationshipId: applicant?.affordableRelationshipId ?? '',
    isPrimary,
    residentInfo,
    contactInformation: applicant?.contactInformation,
    financiallyResponsible:
      applicant?.applicantType?.financiallyResponsible ?? false,
    // Applicant.annualIncome -- same field ApplicationProfile/index.js
    // already reads directly off application.applicants for the legacy
    // modal's reportingInformationInitialValues, so the Mini-Profile doesn't
    // need a second resident-detail fetch the way the resident path does.
    annualIncome: applicant?.annualIncome ?? null,
  };
};

const buildApplicantMinorMember = (applicant) => {
  const minor = applicant?.applicantMinor ?? {};
  const residentInfo = parseResidentInfo(
    applicant,
    ['applicantMinor'],
    'minor',
    false,
    applicant?.relationshipId ?? 'default',
    {},
    minor,
    '',
  );

  return {
    // The Applicant's own id, because that is what getApplicationHousehold
    // puts on the household rows the user clicks -- keying on anything else
    // means freshOccupantInfo never matches and the form opens blank.
    id: applicant.id,
    // The ApplicantMinor's own id, kept separately: PUT
    // .../applicantMinor/{id}/update addresses that record, not the Applicant.
    applicantMinorId: minor?.id ?? applicant.id,
    minorApplicantId: applicant.id,
    applicantId: applicant.id,
    name: [
      minor.firstName,
      minor.lastName,
      minor?.suffix ? minor.suffix.name : '',
    ].join(' '),
    status: 'Pending',
    type: 'minor',
    relationship: applicant?.relationship?.name ?? 'Minor',
    relationshipId: applicant?.relationshipId,
    affordableRelationshipId: applicant?.affordableRelationshipId ?? '',
    residentInfo,
  };
};

export const buildApplicantHouseholdMembers = (applicants = []) => [
  ...(applicants ?? [])
    .filter((applicant) => applicant?.applicantCustomer != null)
    .map(buildApplicantAdultMember),
  ...(applicants ?? [])
    .filter((applicant) => applicant?.applicantMinor != null)
    .map(buildApplicantMinorMember),
];

export default buildApplicantHouseholdMembers;
